import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  Paperclip,
  PenSquare,
  PowerOff,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PageLoader } from "@/components/ui/Spinner";
import { formatDateTime } from "@/utils/formatDate";
import {
  EmptyState,
  PageHeader,
  StatTile,
  Tabs,
} from "@/features/power_projects/components/ui";
import {
  useEopDrafts,
  useEopMessages,
  useEopStatus,
  type EopMessage,
  type EopMessageFilters,
} from "@/features/eop_mail/api/mailbox";
import { DraftCard } from "@/features/eop_mail/components/DraftCard";
import { ThreadPanel } from "@/features/eop_mail/components/ThreadPanel";
import { NewMessageModal } from "@/features/eop_mail/components/NewMessageModal";
import { TaggingScorecard } from "@/features/eop_mail/components/TaggingScorecard";

type Tab =
  | "received"
  | "sent"
  | "all"
  | "unassigned"
  | "mine"
  | "drafts"
  | "quality";

const TABS: { key: Tab; label: string }[] = [
  { key: "received", label: "Odebrane" },
  { key: "sent", label: "Wysłane" },
  { key: "all", label: "Cała skrzynka" },
  { key: "unassigned", label: "Nierozpoznane" },
  { key: "mine", label: "Moje przedsięwzięcia" },
  { key: "drafts", label: "Kopie robocze" },
  { key: "quality", label: "Skuteczność rozpoznawania" },
];

/**
 * Skrzynka eop@elektim.pl — korespondencja z ENERGA.
 *
 * Ekran jest WSPÓLNY dla uprawnionych i celowo nie filtrowany oddziałem:
 * koordynator musi zobaczyć wątek, ZANIM wiadomo, do którego przedsięwzięcia
 * należy. Dlatego „moje" i „nierozpoznane" są zakładkami widoku, a nie granicą
 * dostępu — filtr można zdjąć jednym kliknięciem.
 *
 * Układ dwukolumnowy jak w skrzynce zakupy@: po lewej co przyszło, po prawej
 * jeden wątek w całości. Praca polega na przejrzeniu i przypięciu, więc lista
 * musi zostać na ekranie, gdy czytasz.
 */
export function EopMailboxPage() {
  const [params, setParams] = useSearchParams();
  // Jak w normalnej skrzynce: domyślnie widok odebranych, nie zlepek obu kierunków.
  const [tab, setTab] = useState<Tab>((params.get("tab") as Tab) || "received");
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [search, setSearch] = useState(params.get("q") ?? "");
  const [selectedId, setSelectedId] = useState<string | null>(params.get("id"));
  const [composing, setComposing] = useState(false);

  // Szukanie po każdej literze zasypałoby backend zapytaniami po treści maili.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // ?project= przychodzi z karty przedsięwzięcia („cała skrzynka") — wtedy ekran
  // otwiera się zawężony do tego przedsięwzięcia, zamiast gubić kontekst.
  const projectFilter = params.get("project") ?? "";

  const filters: EopMessageFilters = useMemo(
    () => ({
      q: search,
      unassigned: tab === "unassigned",
      mine: tab === "mine",
      direction:
        tab === "received" ? "incoming" : tab === "sent" ? "outgoing" : undefined,
      project: projectFilter || undefined,
      groupByThread: true,
    }),
    [search, tab, projectFilter],
  );

  const { data: status } = useEopStatus();
  const { data: messages, isLoading } = useEopMessages(filters);
  const { data: waitingDrafts } = useEopDrafts("draft");

  const rows = messages ?? [];
  const selected = useMemo(
    () => rows.find((row) => row.id === selectedId)?.id ?? rows[0]?.id ?? null,
    [rows, selectedId],
  );

  const pick = (id: string) => {
    setSelectedId(id);
    const next = new URLSearchParams(params);
    next.set("id", id);
    setParams(next, { replace: true });
  };

  const switchTab = (key: Tab) => {
    setTab(key);
    setSelectedId(null);
    const next = new URLSearchParams(params);
    next.set("tab", key);
    next.delete("id");
    setParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader
        title="Poczta EOP"
        subtitle={status?.mailbox || "eop@elektim.pl"}
        actions={
          <Button onClick={() => setComposing(true)} disabled={!status?.enabled}>
            <PenSquare size={14} className="mr-1" />
            Nowa wiadomość
          </Button>
        }
      />

      {status && !status.enabled && (
        <div className="mb-4 flex items-start gap-2 border border-[var(--color-primary)] px-3 py-2 text-[12px]">
          <PowerOff size={14} className="mt-0.5 shrink-0 text-[var(--color-primary)]" />
          <div>
            <strong>Skrzynka jest wyłączona.</strong>{" "}
            {status.has_credentials
              ? "Dane logowania są wpięte, ale wyłącznik stoi w pozycji OFF — nic nie wchodzi i nic nie wychodzi."
              : "Nie ma jeszcze danych logowania z Entra — nic nie wchodzi i nic nie wychodzi."}{" "}
            Poniżej widać wyłącznie to, co zdążyło się zaciągnąć wcześniej.
          </div>
        </div>
      )}

      {status?.last_error && (
        <div className="mb-4 flex items-start gap-2 border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>
            Ostatni odbiór zakończył się błędem: <span className="font-mono">{status.last_error}</span>
          </div>
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-6">
        <StatTile
          label="Nieprzeczytane"
          value={status?.messages_unread ?? "—"}
          tone={(status?.messages_unread ?? 0) > 0 ? "blue" : "default"}
          hint="dla całego zespołu"
        />
        <StatTile
          label="Wymaga akcji"
          value={status?.messages_action ?? "—"}
          tone={(status?.messages_action ?? 0) > 0 ? "danger" : "default"}
          hint="flaga zdejmowana ręcznie"
        />
        <StatTile
          label="Wiadomości"
          value={status?.messages_total ?? "—"}
          hint={
            status?.last_poll_at
              ? `odbiór: ${formatDateTime(status.last_poll_at)}`
              : "odbiór jeszcze nie ruszył"
          }
        />
        <StatTile
          label="Nierozpoznane"
          value={status?.messages_unassigned ?? "—"}
          tone={
            (status?.unassigned_ratio ?? 0) > 0.2 &&
            (status?.messages_total ?? 0) >= 20
              ? "danger"
              : "default"
          }
          hint={
            status
              ? `${status.messages_unassigned} z ${status.messages_total} wiadomości`
              : undefined
          }
          onClick={() => switchTab("unassigned")}
          title="Udział nierozpoznanych to miara jakości tagowania, nie zaległość do odhaczenia."
        />
        <StatTile
          label="Czeka na wysyłkę"
          value={status?.drafts_waiting ?? "—"}
          tone={(status?.drafts_waiting ?? 0) > 0 ? "warn" : "default"}
          hint="kopie robocze"
          onClick={() => switchTab("drafts")}
        />
        <StatTile
          label="Stan skrzynki"
          value={status?.enabled ? "włączona" : "wyłączona"}
          tone={status?.enabled ? "ok" : "danger"}
          hint={status?.enabled ? "odbiór co 5 min" : "wyłącznik OFF"}
        />
      </div>

      <Tabs
        tabs={TABS.map((item) => ({
          ...item,
          count:
            item.key === "unassigned"
              ? status?.messages_unassigned ?? 0
              : item.key === "drafts"
                ? status?.drafts_waiting ?? 0
                : 0,
          countTone: "warn" as const,
        }))}
        active={tab}
        onChange={switchTab}
      />
      {tab !== "drafts" && tab !== "quality" && (
        <div className="mb-3 w-full md:w-80">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="szukaj w temacie, nadawcy, treści…"
          />
        </div>
      )}

      {projectFilter && (
        <div className="mb-3 flex items-center gap-2 text-[12px]">
          <Badge color="blue">zawężone do przedsięwzięcia</Badge>
          <button
            type="button"
            className="text-[11px] text-[var(--color-text-muted)] underline hover:text-[var(--color-primary)]"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete("project");
              setParams(next, { replace: true });
            }}
          >
            pokaż całą skrzynkę
          </button>
        </div>
      )}

      {tab === "quality" ? (
        status ? (
          <TaggingScorecard status={status} />
        ) : (
          <PageLoader />
        )
      ) : tab === "drafts" ? (
        <DraftsTab drafts={waitingDrafts} />
      ) : isLoading ? (
        <PageLoader />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[22rem_1fr]">
          <MessageList rows={rows} selectedId={selected} onPick={pick} tab={tab} />
          {selected ? <ThreadPanel key={selected} messageId={selected} /> : <div />}
        </div>
      )}

      <NewMessageModal open={composing} onClose={() => setComposing(false)} />
    </div>
  );
}

function DraftsTab({ drafts }: { drafts?: ReturnType<typeof useEopDrafts>["data"] }) {
  if (!drafts) return <PageLoader />;
  if (drafts.length === 0) {
    return (
      <EmptyState>
        Nic nie czeka na wysyłkę. Kopie robocze powstają przy odpowiadaniu na
        wiadomość i zostają tu, dopóki ktoś ich nie wyśle.
      </EmptyState>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-[12px] text-[var(--color-text-muted)]">
        Te wiadomości są przygotowane, ale <strong>nie wyszły</strong>. Wychodzą
        dopiero po kliknięciu „Wyślij" — nazwisko klikającego zostaje w śladzie.
      </p>
      {drafts.map((draft) => (
        <DraftCard key={draft.id} draft={draft} />
      ))}
    </div>
  );
}

function MessageList({
  rows,
  selectedId,
  onPick,
  tab,
}: {
  rows: EopMessage[];
  selectedId: string | null;
  onPick: (id: string) => void;
  tab: Tab;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState>
        {tab === "unassigned"
          ? "Nic nierozpoznanego — cały wpływ jest przypięty."
          : tab === "mine"
            ? "Na Twoich przedsięwzięciach nie ma jeszcze korespondencji."
            : tab === "sent"
              ? "Nic jeszcze nie wyszło z eop@ — wysłane pojawią się tu razem z tymi z Outlooka."
              : "Skrzynka jest pusta. Wpływ pojawi się tu w ciągu kilku minut od odebrania."}
      </EmptyState>
    );
  }

  return (
    <ul className="h-fit divide-y divide-[var(--color-border)] border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      {rows.map((row) => {
        const waitingOnUs = row.last_move.by === "them";
        const unread = row.thread_unread;
        return (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => onPick(row.id)}
              className={`w-full px-2.5 py-2 text-left ${
                selectedId === row.id
                  ? "bg-[var(--color-bg-elevated)] shadow-[inset_3px_0_0_var(--color-primary)]"
                  : "hover:bg-[var(--color-bg-elevated)]"
              } ${unread ? "bg-blue-50/40" : ""}`}
            >
              <div className="flex items-baseline gap-2">
                {unread && (
                  <span
                    className="h-2 w-2 shrink-0 self-center rounded-full bg-blue-600"
                    title="Nieprzeczytane"
                  />
                )}
                <span
                  className={`truncate text-[13px] ${
                    unread
                      ? "font-bold text-[var(--color-text-primary)]"
                      : waitingOnUs
                        ? "font-semibold text-[var(--color-text-primary)]"
                        : "text-[var(--color-text-secondary)]"
                  }`}
                >
                  {row.subject || "(bez tematu)"}
                </span>
                {row.thread_action && (
                  <Badge color="red" className="shrink-0 whitespace-nowrap">
                    wymaga akcji
                  </Badge>
                )}
                {row.thread_count > 1 && (
                  <span className="shrink-0 font-mono text-[11px] text-[var(--color-text-muted)]">
                    {row.thread_count}
                  </span>
                )}
                <span className="ml-auto shrink-0 font-mono text-[10px] text-[var(--color-text-muted)]">
                  {formatDateTime(row.received_at)}
                </span>
              </div>

              <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-[var(--color-text-muted)]">
                {/* Kto zrobił ostatni ruch — jedyna informacja, po której da się
                    ocenić stan sprawy bez wchodzenia w wątek. */}
                <span className={waitingOnUs ? "text-[var(--color-primary)]" : ""}>
                  {waitingOnUs ? (
                    <ArrowDownLeft size={11} className="inline -mt-px mr-0.5" />
                  ) : (
                    <ArrowUpRight size={11} className="inline -mt-px mr-0.5" />
                  )}
                  {waitingOnUs ? row.last_move.who || row.from_address : "my"}
                </span>
                {row.attachment_count > 0 && (
                  <span className="inline-flex items-center gap-0.5">
                    <Paperclip size={10} />
                    {row.attachment_count}
                  </span>
                )}
                <span className="ml-auto shrink-0">
                  {row.project ? (
                    <span className="font-mono">{row.project.short_name}</span>
                  ) : row.suggested_project ? (
                    <span className="text-[var(--color-primary)]">
                      ? {row.suggested_project.short_name}
                    </span>
                  ) : (
                    <Badge color="orange">nierozpoznane</Badge>
                  )}
                </span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
